import type { TranscriptState } from "../types";

export const POLL_INTERVAL_MS = 3000;

const ALLOWED_EXTS = new Set([
  "mp3", "wav", "m4a", "aac", "ogg", "opus", "flac", "webm", "aiff", "mp4",
]);

// Keep polling the library while anything is still transcribing.
export function shouldPoll(transcripts: { state: TranscriptState }[]): boolean {
  return transcripts.some((t) => t.state === "processing");
}

// Screen reader announcement for transcriptions that settled since the last
// poll. `prev` is null on the first load — nothing is announced then, so
// pre-existing states stay silent. Returns null when nothing changed.
export function transcriptionAnnouncement(
  prev: Map<string, TranscriptState> | null,
  next: { id: string; name: string; state: TranscriptState }[],
): string | null {
  if (!prev) return null;
  for (const t of next) {
    if (prev.get(t.id) !== "processing") continue;
    if (t.state === "done") return `Transcript ready: ${t.name}`;
    if (t.state === "failed") return `Transcription failed: ${t.name}`;
  }
  return null;
}

// Client-side gate mirroring the server's extension allowlist — for fast UX
// feedback, not security (the server re-validates).
export function isAllowedAudioName(name: string): boolean {
  const m = /\.([a-z0-9]{1,5})$/i.exec(name);
  return m ? ALLOWED_EXTS.has(m[1]!.toLowerCase()) : false;
}

// Seconds → "m:ss" (or "h:mm:ss"). Used by the viewer and library.
export function formatTimestamp(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(sec).padStart(2, "0")}`;
}

// Chat is only available for a finished transcript (R13).
export function canChat(state: TranscriptState): boolean {
  return state === "done";
}

export function canRetry(state: TranscriptState): boolean {
  return state !== "processing";
}

export function retryConfirmationMessage(name: string): string {
  return `Retry transcription for "${name}"?\n\nThis will replace the current transcription for this recording.`;
}

// Stable speaker-color *class* (powder → beige → cherry, see .speaker-c* in
// styles.css), assigned by first-seen order within a transcript. Pass a Map to
// keep the assignment consistent across segments.
export function speakerColorClass(speaker: string | null, order: Map<string, number>): string {
  const key = speaker ?? "Speaker";
  if (!order.has(key)) order.set(key, order.size);
  return `speaker-c${order.get(key)! % 3}`;
}

// Translate a raw transcription/API error into a humane one-liner (DESIGN.md
// principle 5 — never show a raw API dump). Falls back to the raw text when
// short and already readable.
export function humaneError(raw: string): string {
  if (/quota|credits? (remaining|required)/i.test(raw))
    return "API quota exceeded. Switch keys or top up credits, then retry.";
  if (/\b401\b|invalid[_ ]?request|api[_ ]?key|unauthor/i.test(raw))
    return "Couldn't reach the transcription service. Check the API key.";
  if (/\b(429|rate)\b/i.test(raw)) return "Rate limited. Wait a moment and retry.";
  return raw.length > 120 ? "Transcription failed. See server logs for details." : raw;
}

// ISO date → short "Jun 24" for the transcript header / library meta.
export function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// USD number → "$1.23" for the admin Costs section.
export function formatUsd(n: number): string {
  return `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
}

// ISO date → local "DD-MM-YY HH:MM:SS" — the precise creation stamp on each row.
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${p(d.getFullYear() % 100)} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
